/**
 * 夥伴詳情 — 停權管理分頁（AC-G-10）。
 *
 * 停權 / 復權都會由後端寫入人工核准 Log（記錄操作者與原因），所以原因欄位在
 * 停權時設為必填 —— 事後查這份 Log 的人需要知道當初為什麼停，「管理員操作」
 * 這種預設值等於沒有記。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from './Button';
import { Field, ModalShell, inputStyle, useEscapeToClose } from './Modal';
import { Pill } from './Pill';
import { listPartnerSuspensionLog, setPartnerStatus, type Partner } from '../lib/api-client';
import { formatDateTime } from '../lib/partner-format';

const ACTION_LABEL: Record<string, string> = {
  suspend: '停權',
  reinstate: '復權',
};

export function PartnerSuspensionTab({ partner }: { partner: Partner }): JSX.Element {
  const [acting, setActing] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['partners.suspensionLog', partner.id],
    queryFn: () => listPartnerSuspensionLog(partner.id),
  });

  const items = data?.items ?? [];

  return (
    <div>
      <div
        style={{
          background: partner.is_active ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${partner.is_active ? '#86efac' : '#fca5a5'}`,
          borderRadius: 6,
          padding: '12px 16px',
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div style={{ fontSize: 13 }}>
          目前狀態：
          {partner.is_active ? (
            <Pill tone="success">啟用中</Pill>
          ) : (
            <Pill tone="danger">已停權</Pill>
          )}
          <div style={{ color: '#6b7280', marginTop: 6 }}>
            {partner.is_active
              ? '停權後該夥伴的所有 API 呼叫會立即回 401，包含進行中的訂票流程。'
              : partner.suspended_reason
                ? `停權原因：${partner.suspended_reason}`
                : '未記錄停權原因。'}
          </div>
        </div>
        <Button
          variant={partner.is_active ? 'danger' : 'primary'}
          onClick={() => setActing(true)}
          style={{ whiteSpace: 'nowrap' }}
        >
          {partner.is_active ? '停權' : '復權'}
        </Button>
      </div>

      <h2 style={{ fontSize: 15, margin: '0 0 8px' }}>人工核准紀錄</h2>
      {isLoading && <div>載入中…</div>}
      {error && <div className="error-banner">{(error as Error).message}</div>}

      {data && (
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th style={{ width: 80 }}>動作</th>
                <th>原因</th>
                <th>核准者</th>
                <th style={{ width: 170 }}>時間</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty-cell">
                    尚無停權 / 復權紀錄
                  </td>
                </tr>
              )}
              {items.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Pill tone={r.action === 'suspend' ? 'danger' : 'success'}>
                      {ACTION_LABEL[r.action] ?? r.action}
                    </Pill>
                  </td>
                  <td>{r.reason}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.approved_by}</td>
                  <td style={{ fontSize: 12 }}>{formatDateTime(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {acting && <StatusModal partner={partner} onClose={() => setActing(false)} />}
    </div>
  );
}

function StatusModal({ partner, onClose }: { partner: Partner; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const suspending = partner.is_active;

  const mutation = useMutation({
    mutationFn: () =>
      setPartnerStatus(partner.id, suspending ? 'suspended' : 'active', reason.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners.detail', partner.id] });
      void queryClient.invalidateQueries({ queryKey: ['partners.suspensionLog', partner.id] });
      void queryClient.invalidateQueries({ queryKey: ['partners.list'] });
      onClose();
    },
  });

  useEscapeToClose(onClose, !mutation.isPending);
  const canSubmit = reason.trim().length > 0 && !mutation.isPending;

  return (
    <ModalShell onBackdropClick={mutation.isPending ? undefined : onClose} width={520}>
      <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>{suspending ? '停權夥伴' : '復權夥伴'}</h2>

      <div
        style={{
          background: suspending ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${suspending ? '#fca5a5' : '#86efac'}`,
          borderLeft: `4px solid ${suspending ? '#dc2626' : '#16a34a'}`,
          borderRadius: 6,
          padding: '10px 14px',
          fontSize: 13,
          marginBottom: 16,
        }}
      >
        {suspending ? (
          <>
            <strong>{partner.display_name}</strong> 的所有 API 呼叫將<strong>立即回 401</strong>，
            包含進行中的訂票流程。既有訂單與票券不受影響。
          </>
        ) : (
          <>
            <strong>{partner.display_name}</strong> 將恢復 API 存取， 原有的 API Key
            與配額設定維持不變。
          </>
        )}
      </div>

      <Field label="原因" hint="會寫入人工核准紀錄；請寫得讓半年後查這筆紀錄的人看得懂">
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={suspending ? '例：合約到期未續約' : '例：已完成續約'}
          style={inputStyle}
          maxLength={256}
          autoFocus
        />
      </Field>

      {mutation.isError && (
        <div className="error-banner" style={{ marginBottom: 12 }}>
          {(mutation.error as Error).message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
          取消
        </Button>
        <Button
          variant={suspending ? 'danger' : 'primary'}
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
        >
          {mutation.isPending ? '處理中…' : suspending ? '確認停權' : '確認復權'}
        </Button>
      </div>
    </ModalShell>
  );
}
